/**
 * Field events (on_change) — client side of the server round-trip.
 *
 * The attribute config (props.on_change) names a handler in the object's
 * module_code; the App fires on VALUE COMMIT (blur for text/number,
 * selection/toggle for the rest), one request in flight PER FIELD is queued
 * last-wins while another field's request is in flight (only one HTTP call
 * outstanding at a time; other committed fields wait their turn, none are
 * dropped), merges only handler-driven diffs, and fails open: an event
 * failure never blocks input or reverts the user's value — before_write at
 * save time is the final judge.
 *
 * Spec: docs/superpowers/specs/2026-08-05-field-events-design.md
 */
import { useRef, useCallback } from 'react'
import { runtimeApi } from '@/app/services/runtimeApi'
import { askInteractionDialog, withInteractionAnswers } from './interactionLoop'

export interface FieldEventTarget {
  field: string
  tabularPart?: string
  rowIndex?: number
}

export function targetKey(t: FieldEventTarget): string {
  return `${t.tabularPart ?? ''}:${t.rowIndex ?? ''}:${t.field}`
}

/** Apply only keys whose server value differs from what we SENT — concurrent
 * user edits to other fields survive the round-trip. Tabular arrays apply
 * wholesale when changed (rare in-flight row edits are last-wins). */
export function mergeFieldEventResponse(
  sent: Record<string, any>,
  received: Record<string, any>,
  current: Record<string, any>,
): Record<string, any> {
  const next = { ...current }
  for (const key of Object.keys(received)) {
    if (JSON.stringify(received[key]) !== JSON.stringify(sent[key])) {
      next[key] = received[key]
    }
  }
  return next
}

interface AttrLike {
  name: string
  props?: Record<string, any>
  columns?: Array<{ name: string; props?: Record<string, any> }>
}

export interface UseFieldEventsOptions {
  connectionId?: string
  objectName: string
  attributes: AttrLike[] | undefined
  getData: () => Record<string, any>
  applyData: (updater: (prev: Record<string, any>) => Record<string, any>) => void
  isSaving: () => boolean
  onError: (text: string) => void
  onMessages?: (messages: string[]) => void
}

export function useFieldEvents(opts: UseFieldEventsOptions) {
  const changedRef = useRef<Set<string>>(new Set())
  const inFlightRef = useRef(false)
  // Keyed by targetKey: same-field re-commits overwrite (last-wins per field),
  // different fields accumulate — Map preserves insertion (commit) order.
  const pendingRef = useRef<Map<string, FieldEventTarget>>(new Map())
  const optsRef = useRef(opts)
  optsRef.current = opts

  const handlerFor = (t: FieldEventTarget): string | undefined => {
    const attrs = optsRef.current.attributes ?? []
    if (t.tabularPart) {
      const tp = attrs.find((a) => a.name.toLowerCase() === t.tabularPart!.toLowerCase())
      const col = (tp?.columns ?? []).find(
        (c) => c.name.toLowerCase() === t.field.toLowerCase(),
      )
      const h = col?.props?.on_change
      return typeof h === 'string' && h.trim() ? h : undefined
    }
    const attr = attrs.find((a) => a.name.toLowerCase() === t.field.toLowerCase())
    const h = attr?.props?.on_change
    return typeof h === 'string' && h.trim() ? h : undefined
  }

  const fire = useCallback(async (target: FieldEventTarget, patch?: Record<string, any>) => {
    const o = optsRef.current
    const connectionId = o.connectionId
    if (!connectionId) return
    inFlightRef.current = true
    const sent = { ...o.getData(), ...(patch ?? {}) }
    try {
      const res = await withInteractionAnswers(
        (answers) =>
          runtimeApi.runFieldEvent(connectionId, o.objectName, {
            field: target.field,
            tabular_part: target.tabularPart ?? null,
            row_index: target.rowIndex ?? null,
            data: sent,
            ...(answers.length ? { interaction_answers: answers } : {}),
          }),
        askInteractionDialog,
      )
      if (optsRef.current.isSaving()) return // never merge into a save in flight
      if (res.error) {
        optsRef.current.onError(res.error)
        return
      }
      if (res.messages?.length) optsRef.current.onMessages?.(res.messages)
      if (res.data) {
        const received = res.data
        optsRef.current.applyData((prev) => mergeFieldEventResponse(sent, received, prev))
      }
    } catch {
      optsRef.current.onError('Field event failed') // fail-open
    } finally {
      inFlightRef.current = false
      // Drain one queued field (oldest commit first); the rest wait for the
      // request this one triggers to finish, one at a time.
      const next = pendingRef.current.keys().next()
      if (!next.done) {
        const key = next.value
        const queued = pendingRef.current.get(key)!
        pendingRef.current.delete(key)
        // Dequeued fire uses FRESH state (no stale patch — state settled by now).
        void fire(queued)
      }
    }
  }, [])

  const markChanged = useCallback((t: FieldEventTarget) => {
    changedRef.current.add(targetKey(t))
  }, [])

  const fireIfChanged = useCallback(
    (target: FieldEventTarget, patch?: Record<string, any>) => {
      const key = targetKey(target)
      if (!changedRef.current.delete(key)) return // nothing changed since last commit
      if (!handlerFor(target)) return // cheap client check — no useless round-trip
      if (inFlightRef.current) {
        pendingRef.current.set(key, target) // last-wins per field, queued by field
        return
      }
      void fire(target, patch)
    },
    [fire],
  )

  return { markChanged, fireIfChanged }
}
